// src/pages/reports/CustomerReturnsReport.jsx
import { useState, useEffect, useMemo } from 'react'
import { ChevronDown, X, Calendar } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import BranchService from '../../services/branchService'

const CustomerReturnsFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(false)

  // Load branches from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        console.log('🔄 CustomerReturnsFilters: Loading filter data...')
        
        // Load branches
        const branchResponse = await BranchService.getAllBranches()
        console.log('📊 CustomerReturnsFilters: Branch API response:', branchResponse)
        
        if (branchResponse.success && branchResponse.data) {
          const branchesData = branchResponse.data.branches || branchResponse.data
          console.log('🏢 CustomerReturnsFilters: Setting branches:', branchesData)
          setBranches(Array.isArray(branchesData) ? branchesData : [])
        } else {
          console.error('❌ CustomerReturnsFilters: Invalid branch response:', branchResponse)
          setBranches([])
        }
      } catch (error) {
        console.error('❌ CustomerReturnsFilters: Error loading data:', error)
        // Set fallback data
        setBranches([])
      } finally {
        setLoading(false)
        console.log('✅ CustomerReturnsFilters: Data loading complete')
      }
    }

    loadData()
  }, [])

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    console.log('🗑️ CustomerReturnsFilters: Clear All clicked')
    onFiltersChange({
      returnType: '',
      refundMethod: '',
      search: '',
      branch: '',
      startDate: '',
      endDate: ''
    })
    onClear()
  }

  const hasActiveFilters = useMemo(() => {
    const active = Boolean(
      (filters?.returnType && filters.returnType !== '') || 
      (filters?.refundMethod && filters.refundMethod !== '') || 
      (filters?.search && filters.search !== '') || 
      (filters?.branch && filters.branch !== '') || 
      (filters?.startDate && filters.startDate !== '') || 
      (filters?.endDate && filters.endDate !== '')
    )
    console.log('🔍 hasActiveFilters check:', { filters, active })
    return active
  }, [filters])

  // Return type options
  const returnTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'customer', label: 'Customer Return' },
    { value: 'internal', label: 'Internal Return' },
    { value: 'damage', label: 'Damage Return' },
    { value: 'expired', label: 'Expired Return' }
  ]

  // Refund method options
  const refundMethodOptions = [
    { value: '', label: 'All Methods' },
    { value: 'cash', label: 'Cash Refund' },
    { value: 'exchange', label: 'Product Exchange' },
    { value: 'credit', label: 'Store Credit' },
    { value: 'card', label: 'Card Refund' }
  ]

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {/* Return Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Return Type
          </label>
          <div className="relative">
            <select
              value={filters?.returnType || ''}
              onChange={(e) => handleFilterChange('returnType', e.target.value)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={loading}
            >
              {returnTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Refund Method Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Refund Method
          </label>
          <div className="relative">
            <select
              value={filters?.refundMethod || ''}
              onChange={(e) => handleFilterChange('refundMethod', e.target.value)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={loading}
            >
              {refundMethodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Search by ID Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search by ID
          </label>
          <input
            type="text"
            placeholder="Order ID or Return ID..."
            value={filters?.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch
          </label>
          <div className="relative">
            <select
              value={filters?.branch || ''}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={loading}
            >
              <option value="">All Branches</option>
              {branches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Start Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={filters?.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* End Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            value={filters?.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>
      </div>

      {loading && (
        <div className="mt-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          <span className="ml-2 text-sm text-gray-600">Loading filters...</span>
        </div>
      )}
    </div>
  )
}

const CustomerReturnsReport = () => {
  // Define columns for the customer returns report
  const columns = [
    { key: 'date', label: 'Date', sortable: true, type: 'date' },
    { key: 'orderId', label: 'Order ID', sortable: true, type: 'text' },
    { key: 'returnType', label: 'Return Type', sortable: true, type: 'text' },
    { key: 'refundMethod', label: 'Refund Method', sortable: true, type: 'text' },
    { key: 'reason', label: 'Reason', sortable: true, type: 'text' },
    { key: 'refundAmount', label: 'Refund Amount', sortable: true, type: 'currency' },
    { key: 'itemCount', label: 'No Items', sortable: true, type: 'number' },
    { key: 'branch', label: 'Branch', sortable: true, type: 'text' }
  ]

  return (
    <BaseReportPage
      title="Customer Returns Report"
      description="Track and analyze customer returns with comprehensive filtering options"
      reportType="customerReturns"
      columns={columns}
      customFilterComponent={CustomerReturnsFilters}
      showExport={true}
      showPrint={true}
    />
  )
}

export default CustomerReturnsReport